import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { MessageService } from 'primeng/api'

import { AuthorizationDetail, AuthorizationPersonType, ChecklistDefinition, ReviewAuthorizationBody } from '@data/interfaces/credit-history.interface'
import { ACTION_LABELS, PERSON_TYPE_LABELS, STATUS_UI, effectiveStatus } from '../../credit-history.labels'
import { ApiCreditHistoryService } from '@data/services/modules/credit-history/api-credit-history.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

const REJECTION_REASON_MIN_LENGTH = 10

/** El formulario de la validación: lo que Legal confirma contra el formato firmado. */
export interface ReviewForm {
  personType: AuthorizationPersonType
  signedAt: string
  name: string
  rfc: string
  legalRepresentative: string
  street: string
  neighborhood: string
  municipality: string
  city: string
  stateCode: string | null
  postalCode: string
  checks: Record<string, boolean>
}

/**
 * Validación de un formato de autorización (plan v4.2 §9.1, D-32): el PDF junto al formulario con los datos del
 * solicitante y la lista de verificación. Aprobar exige todos los puntos que aplican; rechazar, motivo. Solo Legal
 * decide (`credit-history.validate`); Admin ve lo mismo en solo lectura. Los datos se precargan de la ficha, pero
 * lo que vale es lo que dice el formato: el estado y el representante se capturan aquí (decisión del 17-oct).
 */
@Component({
  selector: 'app-credit-validation-detail',
  templateUrl: './validation-detail.component.html',
  styleUrls: ['./validation-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditValidationDetailComponent implements OnInit {
  detail: AuthorizationDetail | null = null
  pdf: Blob | null = null
  pdfError: string | null = null
  loading = false
  loadError: string | null = null
  saving = false
  form: ReviewForm | null = null
  rejecting = false
  rejectionReason = ''

  readonly personTypes = (Object.keys(PERSON_TYPE_LABELS) as AuthorizationPersonType[]).map((value) => ({ value, label: PERSON_TYPE_LABELS[value] }))
  readonly actionLabels = ACTION_LABELS
  readonly personTypeLabels = PERSON_TYPE_LABELS
  readonly reasonMin = REJECTION_REASON_MIN_LENGTH
  readonly canValidate: boolean

  private readonly api = inject(ApiCreditHistoryService)
  private readonly route = inject(ActivatedRoute)
  private readonly messages = inject(MessageService)
  private readonly cd = inject(ChangeDetectorRef)
  private id = ''

  constructor() {
    this.canValidate = inject(AuthService).hasModulePermission('credit-history', 'validate')
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? ''
    this.load()
  }

  get isPending(): boolean {
    return this.detail?.authorization.status === 'pending'
  }

  get editable(): boolean {
    return this.isPending && this.canValidate
  }

  get status() {
    return this.detail ? STATUS_UI[effectiveStatus(this.detail.authorization)] : null
  }

  /** Los puntos que aplican al tipo de persona elegido (el del representante solo a la persona moral). */
  get checks(): ChecklistDefinition[] {
    const type = this.form?.personType
    return (this.detail?.checklist ?? []).filter((item) => !item.onlyFor || item.onlyFor === type)
  }

  get allChecked(): boolean {
    return !!this.form && this.checks.every((item) => this.form!.checks[item.code])
  }

  get missingForApproval(): string[] {
    const form = this.form
    if (!form) return []
    const missing: string[] = []
    if (!form.signedAt) missing.push('la fecha de firma')
    if (!form.name.trim() || !form.rfc.trim()) missing.push('el nombre y el RFC')
    if (form.personType === 'PM' && !form.legalRepresentative.trim()) missing.push('el representante legal')
    if (!form.street.trim() || !form.neighborhood.trim() || !form.municipality.trim() || !form.city.trim()) missing.push('el domicilio completo')
    if (!form.stateCode) missing.push('el estado del catálogo')
    if (!/^\d{5}$/.test(form.postalCode.trim())) missing.push('un CP de 5 dígitos')
    if (!this.allChecked) missing.push('todos los puntos de la lista')
    return missing
  }

  load(): void {
    this.loading = true
    this.loadError = null
    this.api.detail(this.id).subscribe((response) => {
      this.loading = false
      if (response.error || !response.data) {
        this.loadError = response.status === 403 ? 'No tienes permiso para ver esta validación.' : response.msg
      } else {
        this.detail = response.data
        this.form = this.formFrom(response.data)
        this.loadPdf()
      }
      this.cd.markForCheck()
    })
  }

  approve(): void {
    const form = this.form
    if (!form || !this.editable || this.saving || this.missingForApproval.length) return
    const body: ReviewAuthorizationBody = {
      decision: 'approve',
      personType: form.personType,
      signedAt: form.signedAt,
      subject: {
        name: form.name.trim(),
        rfc: form.rfc.trim().toUpperCase(),
        legalRepresentative: form.personType === 'PM' ? form.legalRepresentative.trim() : null,
        address: {
          street: form.street.trim(),
          neighborhood: form.neighborhood.trim(),
          municipality: form.municipality.trim(),
          city: form.city.trim(),
          stateCode: form.stateCode!,
          postalCode: form.postalCode.trim(),
        },
      },
      checklist: this.checks.map((item) => ({ code: item.code, ok: !!form.checks[item.code] })),
    }
    this.submit(body, 'Formato aprobado', 'El cliente ya tiene una autorización vigente.')
  }

  reject(): void {
    const reason = this.rejectionReason.trim()
    if (!this.editable || this.saving || reason.length < REJECTION_REASON_MIN_LENGTH) return
    this.submit({ decision: 'reject', rejectionReason: reason }, 'Formato rechazado', 'Quien lo mandó verá el motivo en la ficha del cliente.')
  }

  trackByIndex(index: number): number {
    return index
  }

  private submit(body: ReviewAuthorizationBody, summary: string, detail: string): void {
    this.saving = true
    this.api.review(this.id, body).subscribe((response) => {
      this.saving = false
      if (response.error) this.messages.add({ key: 'credit-validation', severity: 'error', summary: 'No se guardó la revisión', detail: response.msg, life: 7000 })
      else {
        this.messages.add({ key: 'credit-validation', severity: 'success', summary, detail, life: 5000 })
        this.rejecting = false
        this.load()
      }
      this.cd.markForCheck()
    })
  }

  private loadPdf(): void {
    this.pdf = null
    this.pdfError = null
    this.api.document(this.id).subscribe((response) => {
      if (response.error || !response.data) this.pdfError = response.msg
      else this.pdf = response.data.blob
      this.cd.markForCheck()
    })
  }

  //* Una revisada se ve con lo que se aprobó; una pendiente, con lo precargado de la ficha.
  private formFrom(detail: AuthorizationDetail): ReviewForm {
    const saved = detail.authorization.subject
    const prefill = detail.prefill
    const checks = Object.fromEntries((detail.authorization.checklist ?? []).map((item) => [item.code, item.ok]))
    return {
      personType: detail.authorization.personType ?? prefill.personType,
      signedAt: detail.authorization.signedAt ? detail.authorization.signedAt.slice(0, 10) : '',
      name: saved?.name ?? prefill.name,
      rfc: saved?.rfc ?? prefill.rfc,
      legalRepresentative: saved?.legalRepresentative ?? prefill.legalRepresentative ?? '',
      street: saved?.address.street ?? prefill.address.street,
      neighborhood: saved?.address.neighborhood ?? prefill.address.neighborhood,
      municipality: saved?.address.municipality ?? prefill.address.municipality,
      city: saved?.address.city ?? prefill.address.city,
      stateCode: saved?.address.stateCode ?? prefill.address.stateCode,
      postalCode: saved?.address.postalCode ?? prefill.address.postalCode,
      checks,
    }
  }
}
